import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table } from "antd";
import MainLayout from "../../Layout/mainLayout";
import { viewLeadList } from "../../Redux/Actions/leadActions";
import ListTopSearch from "../../Global Functions/listTopSearch";
import { columnsLeads } from "../../Global Functions/columnsAntd";
import DateRangePopup from "../../Utils/dateFilterPopup";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";


const ListLead = () => {
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });

  const { leadList, loading } = useSelector((state) => state.leads || {});
  const { admin } = useSelector((state) => state.admin || {});
  const accessCode = admin?.data?.access_code || [];
  const canAdd = Array.isArray(accessCode) && accessCode.includes('add');
  const canEdit = Array.isArray(accessCode) && accessCode.includes('edit');

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["5", "10", "20", "50", "100"],
  });

  const fetchDetails = useCallback(async (page, pageSize, showLoader = true) => {
    const payload = {
      page,
      limit: pageSize,
      is_deleted: false,
      filter: debouncedSearch,
      ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
      ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
    };
    try {
      await dispatch(viewLeadList(payload, showLoader));
      if (!showLoader) toast.success("Lead list refreshed");
    } catch {
      if (showLoader) toast.error("Failed to fetch lead list");
    }
  }, [dispatch, debouncedSearch, dateRange]);

  useEffect(() => {
    fetchDetails(pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize, fetchDetails]);

  useEffect(() => {
    if (leadList?.total) {
      setPagination((prev) => ({ ...prev, total: leadList.total }));
    }
  }, [leadList]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const handleTableChange = (pagination) => setPagination(pagination);

  const handleDateRangeApply = (start, end) => {
    if (start === null && end === null) {
      setDateRange({ start: null, end: null });
    } else {
      setDateRange({ start, end });
    }
    setIsDatePopupVisible(false);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleDownloadLeadsExcel = async () => {
    try {
      const data = {
        page: pagination.current,
        limit: pagination.pageSize,
        filter: debouncedSearch,
        ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
        ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
        is_deleted: false,
      };

      const leadData = await dispatch(viewLeadList(data));
      const rawLeadData = leadData?.data || [];
      if (!Array.isArray(rawLeadData) || rawLeadData.length === 0) {
        toast.error("No lead data available to download");
        return;
      }

      const preparedData = rawLeadData.map((lead) => ({
        // _id: lead._id,
        name: lead.name,
        email: lead.email || "",
        country_code: lead.country_code,
        contact_number: lead.contact_number,
        // created_at: lead.created_at,
      }));
      const worksheet = XLSX.utils.json_to_sheet(preparedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
      XLSX.writeFile(workbook, "leads_data.xlsx");
    } catch (error) {
      toast.error("Failed to download lead data");
    }
  };

  return (
    <MainLayout>
      <div className="bookings_table">
        <div className="table_component_all">
          <div style={{ gridColumn: "span 5", height: "auto" }}>
            <ListTopSearch
              h3heading="Lead List"
              buttonhead={canAdd ? "Add Leads" : null}
              navigateTo={canAdd ? "/leads/add" : null}
              setState={setSearchQuery}
              onCalendarClick={() => setIsDatePopupVisible(true)}
              onDownload={canAdd && canEdit ? handleDownloadLeadsExcel : undefined}
            />
          </div>

          <div className="custom_table">
            <div style={{ padding: "0px 60px" }}>
              <Table
                dataSource={(Array.isArray(leadList?.data) && leadList.data) || []}
                columns={columnsLeads(canEdit)}
                pagination={pagination}
                loading={loading}
                onChange={handleTableChange}
                scroll={{ x: 1400, y: 500 }}
                rowKey="_id"
                rowClassName={() => "text-center"}
              />
            </div>
          </div>
        </div>
      </div>

      <DateRangePopup
        visible={isDatePopupVisible}
        initialStart={dateRange.start}
        initialEnd={dateRange.end}
        onCancel={() => setIsDatePopupVisible(false)}
        onOk={handleDateRangeApply}
      />
    </MainLayout>
  );
};

export default ListLead;